import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import {
  getYoga,
  deleteYoga,
  createYoga,
  updateYoga,
} from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  {
    key: "shortDescription",
    label: "Summary",
    render: (value) => (value ? String(value).slice(0, 120) : "-"),
  },
  {
    key: "status",
    label: "Status",
    render: (value) => (value ? String(value) : "draft"),
  },
  {
    key: "publishDate",
    label: "Publish Date",
    render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
];

const filters = [{ key: "author", label: "Filter by Author", type: "search" }];

const initialFormData = {
  title: "",
  author: "Maha Sabha",
  shortDescription: "",
  content: "",
  status: "draft",
  publishDate: "",
  imageUrlsText: "",
  videoUrlsText: "",
};

const splitLines = (raw = "") =>
  String(raw)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function Yoga() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [coverImage, setCoverImage] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [existingMedia, setExistingMedia] = useState([]);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData(initialFormData);
    setCoverImage("");
    setCoverFile(null);
    setExistingMedia([]);
  };

  const handleAdd = () => {
    resetForm();
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    const mediaItems = Array.isArray(item.mediaItems) ? item.mediaItems : [];
    const hostedMedia = mediaItems.filter(
      (m) => m?.url && /^https?:\/\//i.test(m.url)
    );
    setSelectedItem(item);
    setFormData({
      title: item.title || "",
      author: item.author || "Maha Sabha",
      shortDescription: item.shortDescription || "",
      content: item.content || "",
      status: item.status === "published" ? "published" : "draft",
      publishDate: toDateInput(item.publishDate),
      imageUrlsText: "",
      videoUrlsText: "",
    });
    setExistingMedia(hostedMedia);
    setCoverImage(item.imageUrl && /^https?:\/\//i.test(item.imageUrl) ? item.imageUrl : "");
    setCoverFile(null);
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (item) => handleEdit(item);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverImage(reader.result);
    reader.readAsDataURL(file);
  };

  const removeExistingMedia = (url) => {
    setExistingMedia((prev) => prev.filter((m) => m.url !== url));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("title", formData.title.trim());
      payload.append("author", formData.author.trim() || "Maha Sabha");
      payload.append("shortDescription", formData.shortDescription.trim());
      payload.append("content", formData.content.trim());
      payload.append("status", formData.status);
      if (formData.publishDate) {
        payload.append(
          "publishDate",
          new Date(`${formData.publishDate}T12:00:00`).toISOString()
        );
      }

      const extraImages = splitLines(formData.imageUrlsText).filter((u) =>
        /^https?:\/\//i.test(u)
      );
      const extraVideos = splitLines(formData.videoUrlsText).filter((u) =>
        /^https?:\/\//i.test(u)
      );
      if (existingMedia.length) {
        payload.append("mediaItems", JSON.stringify(existingMedia));
      }
      if (extraImages.length) {
        payload.append("imageUrls", JSON.stringify(extraImages));
      }
      if (extraVideos.length) {
        payload.append("videoUrls", JSON.stringify(extraVideos));
      }
      if (coverFile) {
        payload.append("mediaFiles", coverFile);
      } else if (coverImage && /^https?:\/\//i.test(coverImage)) {
        payload.append("imageUrl", coverImage);
      }

      if (isEditMode && selectedItem) {
        await updateYoga(selectedItem._id, payload);
        alert("Yoga session updated successfully!");
      } else {
        await createYoga(payload);
        alert("Yoga session created successfully!");
      }
      setModalOpen(false);
      setCoverFile(null);
    } catch (error) {
      alert(error.message || "Failed to save Yoga session");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Yoga"
          fetchData={getYoga}
          deleteData={deleteYoga}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search yoga sessions..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Yoga Session" : "Add New Yoga Session"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                  placeholder="Session title"
                />
              </div>
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleFormChange}
                  placeholder="Author name"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="form-group">
                <label>Publish Date</label>
                <input
                  type="date"
                  name="publishDate"
                  value={formData.publishDate}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Short Description *</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleFormChange}
                rows="3"
                required
                placeholder="Short summary for cards and notifications"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Content</h3>
            <div className="form-group">
              <label>Content *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleFormChange}
                rows="10"
                required
                placeholder="Describe the yoga session"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Media</h3>
            <div className="form-group">
              <label>Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {coverImage && (
                <div className="image-preview">
                  <img
                    src={coverImage}
                    alt="Preview"
                    style={{ maxWidth: "120px", maxHeight: "120px", borderRadius: "8px" }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCoverImage("");
                      setCoverFile(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {existingMedia.length > 0 && (
              <div className="form-group">
                <label>Existing Media</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {existingMedia.map((m) => (
                    <div key={m.url} style={{ textAlign: "center" }}>
                      {m.type === "video" ? (
                        <span style={{ fontSize: 12 }}>🎬 Video</span>
                      ) : (
                        <img
                          src={m.url}
                          alt="media"
                          style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 6 }}
                        />
                      )}
                      <button
                        type="button"
                        style={{ display: "block", marginTop: 4, fontSize: 11 }}
                        onClick={() => removeExistingMedia(m.url)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Extra Image URLs (one per line)</label>
              <textarea
                name="imageUrlsText"
                value={formData.imageUrlsText}
                onChange={handleFormChange}
                rows="3"
                placeholder={"https://image-1.jpg\nhttps://image-2.jpg"}
              />
            </div>
            <div className="form-group">
              <label>Video URLs (one per line)</label>
              <textarea
                name="videoUrlsText"
                value={formData.videoUrlsText}
                onChange={handleFormChange}
                rows="3"
                placeholder="https://video-1.mp4"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : isEditMode ? "Update Session" : "Create Session"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
